import { baseApi } from '@/lib';

import {
  CreateSurveyPayload,
  Survey,
  SurveyListResponse,
  SurveySaveResponse,
  UpdateSurveyRequest,
} from '../types';

const enhancedApi = baseApi.enhanceEndpoints({
  addTagTypes: ['Surveys'],
});

export const surveysApi = enhancedApi.injectEndpoints({
  endpoints: (builder) => ({
    getSurveys: builder.query<SurveyListResponse, void>({
      query: () => 'surveys',
      providesTags: (result) =>
        result
          ? [
              ...result.map((survey) => ({ type: 'Surveys' as const, id: survey.globalId })),
              { type: 'Surveys' as const, id: 'LIST' },
            ]
          : [{ type: 'Surveys' as const, id: 'LIST' }],
    }),
    getSurvey: builder.query<Survey, string>({
      query: (globalId) => `surveys/${globalId}`,
      providesTags: (result, _error, globalId) => [
        {
          type: 'Surveys',
          id: result?.globalId ?? globalId,
        },
      ],
    }),
    createSurvey: builder.mutation<SurveySaveResponse, CreateSurveyPayload>({
      query: (body) => ({
        url: 'surveys',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Surveys', id: 'LIST' }],
    }),
    updateSurvey: builder.mutation<SurveySaveResponse, UpdateSurveyRequest>({
      query: ({ globalId, payload }) => ({
        url: `surveys/${globalId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Surveys' as const, id: 'LIST' },
              { type: 'Surveys' as const, id: result.globalId },
            ]
          : [{ type: 'Surveys' as const, id: 'LIST' }],
    }),
    republishSurvey: builder.mutation<SurveySaveResponse, string>({
      query: (globalId) => ({
        url: `surveys/${globalId}/republish`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, globalId) => [
        { type: 'Surveys', id: 'LIST' },
        { type: 'Surveys', id: globalId },
      ],
    }),
    deleteSurvey: builder.mutation<void, string>({
      query: (globalId) => ({
        url: `surveys/${globalId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, globalId) => [
        { type: 'Surveys', id: 'LIST' },
        { type: 'Surveys', id: globalId },
      ],
    }),
  }),
});

export const {
  useGetSurveysQuery,
  useGetSurveyQuery,
  useCreateSurveyMutation,
  useUpdateSurveyMutation,
  useRepublishSurveyMutation,
  useDeleteSurveyMutation,
} = surveysApi;
